import { useCallback } from "react";
import { useWorkflowContext } from "../../WorkflowContextProvider";
import { useVariablesContext } from "../context/variableContext";
import { buildVariablePayload } from "../utils/payloadBuilder";

/** Team chips / API use the same attribute builders as this hook; import from here or from `../utils/payloadBuilder`. */
export { getDefaultAttributesForChipType } from "../utils/payloadBuilder";

/**
 * Custom hook for building variable payloads from context
 * @author Abhishek Dhillon
 * @returns {Object} Hook functions for building payloads
 */
export const usePayloadBuilder = () => {
  const contextValues = useVariablesContext();
  const { selectedVariable, workflow } = useWorkflowContext();
  /**
   * Builds payload for dropdown type variables
   * @returns {Object} Complete payload object for dropdown variables
   */
  const buildDropdownPayload = useCallback(() => {
    return buildVariablePayload(contextValues, selectedVariable, workflow, "dropdown");
  }, [contextValues, selectedVariable, workflow]);

  /**
   * Builds payload for text type variables
   * @returns {Object} Complete payload object for text variables
   */
  const buildTextPayload = useCallback(() => {
    return buildVariablePayload(contextValues, selectedVariable, workflow, "text");
  }, [contextValues, selectedVariable, workflow]);

  /**
   * Builds payload for date type variables
   * @returns {Object} Complete payload object for date variables
   */
  const buildDatePayload = useCallback(() => {
    return buildVariablePayload(contextValues, selectedVariable, workflow, "date");
  }, [contextValues, selectedVariable, workflow]);

  /**
   * Builds payload for number type variables
   * @returns {Object} Complete payload object for number variables
   */
  const buildNumberPayload = useCallback(() => {
    return buildVariablePayload(contextValues, selectedVariable, workflow, "number");
  }, [contextValues, selectedVariable, workflow]);

  /**
   * Builds payload for any variable type
   * @param {string} variableType - The type of variable (dropdown, text, date, number)
   * @returns {Object} Complete payload object
   */
  const buildPayload = useCallback(
    (variableType) => {
      return buildVariablePayload(contextValues, selectedVariable, workflow, variableType);
    },
    [contextValues, selectedVariable, workflow]
  );

  /**
   * Gets only the attributes portion of the payload
   * @param {string} variableType - The type of variable
   * @returns {Object} Attributes object only
   */
  const getAttributes = useCallback(
    (variableType) => {
      const payload = buildVariablePayload(contextValues, selectedVariable, workflow, variableType);
      return payload.attributes;
    },
    [contextValues, selectedVariable, workflow]
  );

  return {
    buildDropdownPayload,
    buildTextPayload,
    buildDatePayload,
    buildNumberPayload,
    buildPayload,
    getAttributes,
    contextValues, // Expose context values if needed
  };
};
